// Main-thread bootstrap: skirmish setup, worker handshake, render loop, input
// modes (select / place / road / claim / survey / rally), drag-box unit
// selection, RMB move/attack-move orders, control groups, building inspection,
// saves, game-over overlay.

import waresJson from '../data/wares.json';
import buildingsJson from '../data/buildings.json';
import { GameAudio } from './audio';
import { GameScene } from './render/scene';
import { BUILDING_RECIPE_LABELS } from './render/views';
import { Hud, UNIT_NAMES, type HudState, type UiMode } from './ui/hud';
import { PauseMenu } from './ui/menu';
import { Minimap } from './ui/minimap';
import { showSetup } from './ui/setup';
import { Tutorial, type TutorialCtx } from './ui/tutorial';
import { listSaves, loadGame, saveGame } from './ui/saves';
import { MAP_SIZES, UnitKind, type MapSizeName } from './shared/constants';
import {
  BUILDING_STRIDE,
  UNIT_STRIDE,
  defaultConfig,
  type AiDifficulty,
  type Command,
  type GameConfig,
  type MainToWorker,
  type Replay,
  type WinCondition,
  type WorkerToMain,
} from './shared/protocol';

const WARE_IDS = waresJson.wares.map((w) => w.id);
const WARE_NAMES = waresJson.wares.map((w) => w.name.toLowerCase());
const BUILDING_IDS = buildingsJson.buildings.map((b) => b.id);
const BUILDING_NAMES = buildingsJson.buildings.map((b) => b.name);
const BUILDING_GARRISON = buildingsJson.buildings.map(
  (b) => ((b as Record<string, unknown>).garrison as number) ?? 0,
);

/** config from URL params; null when no ?seed → show the setup screen */
function configFromUrl(): GameConfig | null {
  const p = new URLSearchParams(location.search);
  const seedRaw = p.get('seed');
  if (seedRaw === null) return null;
  const seed = Number.parseInt(seedRaw, 10);
  const cfg = defaultConfig(
    Number.isFinite(seed) ? seed >>> 0 : (Date.now() & 0x7fffffff) >>> 0,
    (p.get('size') as MapSizeName | null) && p.get('size')! in MAP_SIZES
      ? (p.get('size') as MapSizeName)
      : 'medium',
  );
  cfg.aiDifficulty = (
    ['none', 'easy', 'normal', 'hard'].includes(p.get('ai') ?? '') ? p.get('ai') : 'normal'
  ) as AiDifficulty;
  cfg.winCondition = (
    ['conquest', 'monument', 'score'].includes(p.get('win') ?? '') ? p.get('win') : 'conquest'
  ) as WinCondition;
  cfg.mirror = p.get('mirror') === '1';
  const faction = Number.parseInt(p.get('faction') ?? '', 10);
  if (faction >= 0 && faction <= 2) cfg.playerFaction = faction;
  const minutes = Number.parseInt(p.get('minutes') ?? '', 10);
  if ([20, 40, 60].includes(minutes)) cfg.scoreMinutes = minutes;
  return cfg;
}

function writeUrl(config: GameConfig): void {
  const url = new URL(location.href);
  url.searchParams.set('seed', String(config.seed));
  url.searchParams.set('size', config.mapSize);
  url.searchParams.set('ai', config.aiDifficulty);
  url.searchParams.set('win', config.winCondition);
  if (config.mirror) url.searchParams.set('mirror', '1');
  else url.searchParams.delete('mirror');
  url.searchParams.set('faction', String(config.playerFaction));
  if (config.winCondition === 'score') url.searchParams.set('minutes', String(config.scoreMinutes));
  history.replaceState(null, '', url.toString());
}

const canvas = document.getElementById('game') as HTMLCanvasElement;
const hudRoot = document.getElementById('hud')!;

const scene = new GameScene(canvas);
const worker = new Worker(new URL('./sim/worker.ts', import.meta.url), { type: 'module' });

// audio starts on the first user gesture (autoplay policy); idempotent
const audio = new GameAudio();
const ensureAudio = (): void => audio.ensure();
window.addEventListener('pointerdown', ensureAudio, { once: true });
window.addEventListener('keydown', ensureAudio, { once: true });

// tutorial progress flags fed from player input (M5)
const tutFlags = { camMoved: false, roadBuilt: false, claimed: false, surveyed: false };

function send(msg: MainToWorker): void {
  worker.postMessage(msg);
}

function sendCommands(commands: Command[]): void {
  for (const c of commands) {
    if (c.type === 'placeBuilding') audio.place();
    else if (c.type === 'buildRoad') {
      audio.road();
      tutFlags.roadBuilt = true;
    } else if (c.type === 'claimHex') tutFlags.claimed = true;
    else if (c.type === 'surveyHex') tutFlags.surveyed = true;
  }
  send({ type: 'commands', commands });
}

let mode: UiMode = { kind: 'select' };
let inspectingId = -1;
let pendingSaveName: string | null = null;
let rmbAttack = true; // DESIGN.md §7.5: attack-move is the default RMB order
const controlGroups = new Map<number, number[]>();

const hudState: HudState = {
  tick: 0,
  fps: 0,
  simMsPerTick: 0,
  checksum: 0,
  seed: 0,
  speed: 1,
  hovered: null,
  stocks: [],
  popUsed: 0,
  popCap: 0,
  surveyorsIdle: 0,
  surveyorsTotal: 0,
  openRequests: 0,
  mode,
  flowOverlay: false,
  inspection: null,
  selection: null,
  rmbAttack: true,
  result: null,
  scores: [0, 0],
  scoreTicksLeft: -1,
  researchedMask: 0,
};

function setMode(m: UiMode): void {
  mode = m;
  hudState.mode = m;
  if (m.kind !== 'place') scene.hideGhost();
}

function setInspect(id: number): void {
  inspectingId = id;
  scene.setSelectedBuilding(id);
  send({ type: 'inspect', building: id });
  if (id === -1) {
    hudState.inspection = null;
    scene.setRallyMarker(-1);
  }
}

function selectUnits(ids: number[]): void {
  scene.selectedUnits.clear();
  for (const id of ids) scene.selectedUnits.add(id);
  if (ids.length > 0) setInspect(-1); // unit selection replaces building inspection
}

function doSave(name: string): void {
  pendingSaveName = name;
  send({ type: 'getReplay' });
}

function doLoad(name: string): void {
  void loadGame(name).then((entry) => {
    if (!entry) return;
    const replay = entry.replay as Replay;
    if (replay.version !== 5 || !replay.config) {
      hud.toast(`'${name}' is from an older version and cannot be loaded`);
      return;
    }
    setInspect(-1);
    selectUnits([]);
    send({ type: 'load', replay, untilTick: entry.tick });
    hud.toast(`loading '${name}'…`);
  });
}

function refreshSaves(): void {
  void listSaves().then((names) => {
    hud.setSaves(names);
    menu.setSaves(names);
  });
}

const hud = new Hud(
  hudRoot,
  0,
  WARE_IDS,
  WARE_NAMES,
  BUILDING_IDS,
  BUILDING_NAMES,
  BUILDING_RECIPE_LABELS,
  {
    onSpeed(speed) {
      hudState.speed = speed;
      send({ type: 'setSpeed', speed });
    },
    onNewSeed(newSeed) {
      const url = new URL(location.href);
      url.searchParams.set('seed', String(newSeed));
      location.href = url.toString();
    },
    onMode(m) {
      setMode(
        mode.kind === m.kind && (m.kind !== 'place' || (mode as { def?: string }).def === m.def)
          ? { kind: 'select' }
          : m,
      );
    },
    onTrainSurveyor() {
      sendCommands([{ type: 'trainSurveyor' }]);
    },
    onTrainScout() {
      sendCommands([{ type: 'trainScout' }]);
    },
    onWarePriority(ware) {
      sendCommands([{ type: 'setWarePriority', ware }]);
    },
    onSetPriority(building, level) {
      sendCommands([{ type: 'setPriority', building, level }]);
    },
    onSetRecipe(building, recipe) {
      sendCommands([{ type: 'setRecipe', building, recipe }]);
    },
    onRecruit(building, kind, count) {
      sendCommands([{ type: 'recruit', building, kind, count }]);
    },
    onVeteran(building, on) {
      sendCommands([{ type: 'setVeteran', building, on }]);
    },
    onUngarrison(building) {
      sendCommands([{ type: 'ungarrison', building }]);
    },
    onSetResearch(building, node) {
      sendCommands([{ type: 'setResearch', building, node }]);
    },
    onRmbToggle(on) {
      rmbAttack = on;
      hudState.rmbAttack = on;
    },
    onCloseInspect() {
      setInspect(-1);
    },
    onAlertJump(alert) {
      if (scene.cam) scene.cam.target.set(alert.x, 0, alert.y);
    },
    onSave(name) {
      doSave(name);
    },
    onLoad(name) {
      doLoad(name);
    },
    onMenu() {
      toggleMenu();
    },
  },
);

// ---- M5: minimap, tutorial hints, pause menu ----

const minimap = new Minimap(hudRoot, (x, y) => {
  if (scene.cam) scene.cam.target.set(x, 0, y);
  tutFlags.camMoved = true;
});
const tutorial = new Tutorial(hudRoot);

let speedBeforeMenu = 1;
const menu = new PauseMenu(
  hudRoot,
  {
    onResume() {
      hudState.speed = speedBeforeMenu;
      send({ type: 'setSpeed', speed: speedBeforeMenu as 0 | 1 | 2 | 3 });
    },
    onSave(name) {
      doSave(name);
    },
    onLoad(name) {
      doLoad(name);
    },
    onVolume(v) {
      audio.ensure();
      audio.setVolume(v);
    },
    onMuted(m) {
      audio.setMuted(m);
    },
    onHints(on) {
      tutorial.setEnabled(on);
    },
    onNewGame() {
      const url = new URL(location.href);
      url.search = '';
      location.href = url.toString();
    },
  },
  () => ({ volume: audio.volume, muted: audio.muted }),
  () => tutorial.isEnabled,
);

function toggleMenu(): void {
  if (menu.isOpen) {
    menu.close(); // close() fires onResume
    return;
  }
  speedBeforeMenu = hudState.speed;
  hudState.speed = 0;
  send({ type: 'setSpeed', speed: 0 });
  menu.open();
}

/** player state summary for the tutorial predicates (cheap; ~2 Hz) */
function buildTutorialCtx(): TutorialCtx {
  const counts = new Map<string, number>();
  const b = scene.buildingsSnapshot;
  if (b) {
    for (let i = 0; i < b.count; i++) {
      const o = i * BUILDING_STRIDE;
      if (b.buildings[o + 10] !== 0) continue;
      const id = BUILDING_IDS[b.buildings[o + 1]];
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  let soldiers = 0;
  const u = scene.unitsSnapshot;
  if (u) {
    for (let i = 0; i < u.count; i++) {
      const o = i * UNIT_STRIDE;
      const kind = u.units[o + 1];
      if (u.units[o + 7] === 0 && kind >= UnitKind.Shieldbearer && kind <= UnitKind.Sapper) {
        soldiers++;
      }
    }
  }
  return {
    counts,
    soldiers,
    camMoved: tutFlags.camMoved,
    roadBuilt: tutFlags.roadBuilt,
    claimed: tutFlags.claimed,
    surveyed: tutFlags.surveyed,
    won: hudState.result?.winner === 0,
  };
}

refreshSaves();

worker.onmessage = (ev: MessageEvent<WorkerToMain>) => {
  const msg = ev.data;
  switch (msg.type) {
    case 'map':
      scene.setMap(msg);
      minimap.reset(msg.info.width, msg.info.height);
      break;
    case 'snapshot':
      hudState.tick = msg.tick;
      scene.onSnapshot(msg);
      break;
    case 'events':
      scene.onEvents(msg);
      if (msg.tilesChanged.length > 0 || msg.territory || msg.fog) minimap.markDirty();
      if (msg.rejects.length > 0) audio.reject();
      if (msg.alerts.length > 0) audio.ping();
      for (const a of msg.alerts) minimap.ping(a.x, a.y);
      if (msg.shots.length > 0) audio.shots(msg.shots.length / 5);
      if (msg.deaths.length > 0) audio.death();
      for (const r of msg.rejects) hud.toast(r);
      for (const a of msg.alerts) hud.toast(a.text, a);
      break;
    case 'stats':
      if (msg.result && !hudState.result) {
        if (msg.result.winner === 0) audio.victory();
        else if (msg.result.winner === -1) audio.chime();
        else audio.defeat();
      }
      hudState.checksum = msg.checksum;
      hudState.simMsPerTick = msg.simMsPerTick;
      hudState.stocks = msg.stocks;
      hudState.popUsed = msg.popUsed;
      hudState.popCap = msg.popCap;
      hudState.surveyorsIdle = msg.surveyorsIdle;
      hudState.surveyorsTotal = msg.surveyorsTotal;
      hudState.openRequests = msg.openRequests;
      hudState.result = msg.result;
      hudState.scores = msg.scores;
      hudState.scoreTicksLeft = msg.scoreTicksLeft;
      hudState.researchedMask = msg.researchedMask;
      break;
    case 'inspection':
      if (msg.id === inspectingId) {
        hudState.inspection = msg;
        scene.setRallyMarker(msg.queue ? msg.rallyTile : -1);
      }
      break;
    case 'loaded':
      hud.toast(`loaded (tick ${msg.tick})`);
      break;
    case 'replay':
      if (pendingSaveName) {
        const name = pendingSaveName;
        pendingSaveName = null;
        void saveGame({
          name,
          savedAt: Date.now(),
          tick: hudState.tick,
          replay: msg.replay,
        }).then(() => {
          hud.toast(`saved '${name}'`);
          refreshSaves();
        });
      } else {
        console.log('replay', msg.replay);
      }
      break;
  }
};

// ---- boot: URL config auto-starts (e2e/replays); otherwise the setup screen ----

function startGame(config: GameConfig): void {
  hudState.seed = config.seed;
  hud.setSeed(config.seed);
  send({ type: 'init', config });
}

const urlConfig = configFromUrl();
if (urlConfig) {
  startGame(urlConfig);
} else {
  const defaults = defaultConfig((Date.now() & 0x7fffffff) >>> 0, 'medium');
  defaults.aiDifficulty = 'normal'; // skirmish vs AI is the headline mode (M4)
  showSetup(document.body, defaults, (config) => {
    writeUrl(config);
    startGame(config);
  });
}

// ---- input ----

let roadDrag: Set<number> | null = null;
let lastDragTile: number | null = null;
// drag-box selection state (select mode, LMB)
let boxStart: { x: number; y: number } | null = null;
let boxDragging = false;
const boxEl = document.createElement('div');
boxEl.style.cssText =
  'position:fixed;border:1px solid #7fff9f;background:rgba(127,255,159,0.12);' +
  'pointer-events:none;display:none;z-index:40';
document.body.appendChild(boxEl);

function tileIndex(t: { x: number; y: number }): number {
  return t.y * scene.mapW + t.x;
}

/** add the straight segment from the previously hovered drag tile (Bresenham) */
function addRoadTiles(from: number | null, to: { x: number; y: number }): void {
  if (!roadDrag) return;
  if (from === null) {
    roadDrag.add(tileIndex(to));
    return;
  }
  let x0 = from % scene.mapW;
  let y0 = Math.floor(from / scene.mapW);
  const dx = Math.abs(to.x - x0);
  const dy = Math.abs(to.y - y0);
  const sx = x0 < to.x ? 1 : -1;
  const sy = y0 < to.y ? 1 : -1;
  let err = dx - dy;
  for (;;) {
    roadDrag.add(y0 * scene.mapW + x0);
    if (x0 === to.x && y0 === to.y) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }
}

canvas.addEventListener('contextmenu', (e) => e.preventDefault());

canvas.addEventListener('pointermove', (e) => {
  const tile = scene.pick(e.clientX, e.clientY);
  hudState.hovered = tile;
  if (boxStart) {
    const dx = e.clientX - boxStart.x;
    const dy = e.clientY - boxStart.y;
    if (boxDragging || dx * dx + dy * dy > 25) {
      boxDragging = true;
      boxEl.style.display = 'block';
      boxEl.style.left = `${Math.min(boxStart.x, e.clientX)}px`;
      boxEl.style.top = `${Math.min(boxStart.y, e.clientY)}px`;
      boxEl.style.width = `${Math.abs(dx)}px`;
      boxEl.style.height = `${Math.abs(dy)}px`;
    }
    return;
  }
  if (!tile) return;
  if (mode.kind === 'place') {
    scene.showGhost(tile.x, tile.y, mode.defIdx);
  } else if (mode.kind === 'road' && roadDrag) {
    addRoadTiles(lastDragTile, tile);
    lastDragTile = tileIndex(tile);
  }
});

canvas.addEventListener('pointerdown', (e) => {
  // RMB: orders for the selected soldiers (DESIGN.md §7.5)
  if (e.button === 2) {
    if (scene.selectedUnits.size === 0) return;
    const ids = [...scene.selectedUnits].sort((a, b) => a - b);
    const bId = scene.pickBuilding(e.clientX, e.clientY);
    if (bId !== -1) {
      const info = scene.buildingInfo(bId);
      if (info && info.owner === 0 && (BUILDING_GARRISON[info.defIdx] ?? 0) > 0) {
        sendCommands([{ type: 'garrisonUnits', ids, building: bId }]);
        return;
      }
    }
    const tile = scene.pick(e.clientX, e.clientY);
    if (tile) {
      const attack = e.ctrlKey ? !rmbAttack : rmbAttack; // Ctrl inverts the default
      sendCommands([{ type: 'moveUnits', ids, tile: tileIndex(tile), attack }]);
    }
    return;
  }
  if (e.button !== 0) return;
  const tile = scene.pick(e.clientX, e.clientY);
  switch (mode.kind) {
    case 'select':
      boxStart = { x: e.clientX, y: e.clientY };
      boxDragging = false;
      break;
    case 'place':
      if (tile) sendCommands([{ type: 'placeBuilding', def: mode.def, x: tile.x, y: tile.y }]);
      break;
    case 'road':
      if (tile) {
        roadDrag = new Set([tileIndex(tile)]);
        lastDragTile = tileIndex(tile);
      }
      break;
    case 'claim':
    case 'survey': {
      if (!tile) break;
      const hex = scene.hexOfTile ? scene.hexOfTile[tileIndex(tile)] : -1;
      if (hex >= 0) {
        sendCommands([{ type: mode.kind === 'claim' ? 'claimHex' : 'surveyHex', hex }]);
      }
      break;
    }
    case 'rally': {
      if (tile) {
        sendCommands([{ type: 'setRally', building: mode.building, tile: tileIndex(tile) }]);
        hud.toast('rally point set');
        setMode({ kind: 'select' });
      }
      break;
    }
  }
});

window.addEventListener('pointerup', (e) => {
  if (e.button !== 0) return;
  if (mode.kind === 'road' && roadDrag && roadDrag.size > 0) {
    sendCommands([{ type: 'buildRoad', tiles: [...roadDrag].sort((a, b) => a - b) }]);
  }
  roadDrag = null;
  lastDragTile = null;
  if (boxStart) {
    if (boxDragging) {
      selectUnits(scene.unitsInRect(boxStart.x, boxStart.y, e.clientX, e.clientY));
    } else {
      // click: prefer a unit under the cursor, else a building, else deselect
      const unitId = scene.pickUnit(e.clientX, e.clientY);
      if (unitId !== -1) {
        selectUnits([unitId]);
      } else {
        selectUnits([]);
        setInspect(scene.pickBuilding(e.clientX, e.clientY));
      }
    }
    boxStart = null;
    boxDragging = false;
    boxEl.style.display = 'none';
  }
});

window.addEventListener('keydown', (e) => {
  if (e.code === 'Escape') {
    if (menu.isOpen) {
      menu.close();
      return;
    }
    // cancel whatever is active; with nothing to cancel, open the pause menu
    const cancelable =
      mode.kind !== 'select' || scene.selectedUnits.size > 0 || inspectingId !== -1;
    setMode({ kind: 'select' });
    setInspect(-1);
    selectUnits([]);
    if (!cancelable) toggleMenu();
  } else if (e.code === 'Space') {
    e.preventDefault();
    const next = hudState.speed === 0 ? 1 : 0;
    hudState.speed = next;
    send({ type: 'setSpeed', speed: next as 0 | 1 });
  } else if (e.code === 'Minus' || e.code === 'Equal') {
    // speed control moved off the digits: digits are control groups (§7.5)
    const cur = hudState.speed;
    const next = e.code === 'Minus' ? Math.max(0, cur - 1) : Math.min(3, cur + 1);
    hudState.speed = next;
    send({ type: 'setSpeed', speed: next as 0 | 1 | 2 | 3 });
  } else if (e.code.startsWith('Digit') && e.code !== 'Digit0') {
    const n = Number(e.code.slice(-1));
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      controlGroups.set(n, [...scene.selectedUnits]);
      hud.toast(`group ${n} assigned (${scene.selectedUnits.size})`);
    } else if (controlGroups.has(n)) {
      selectUnits(controlGroups.get(n)!);
    }
  } else if (e.code === 'KeyF') {
    hudState.flowOverlay = scene.toggleFlow();
  } else if (
    e.code === 'KeyW' ||
    e.code === 'KeyA' ||
    e.code === 'KeyS' ||
    e.code === 'KeyD' ||
    e.code.startsWith('Arrow')
  ) {
    tutFlags.camMoved = true;
  }
});

// tutorial "look around" completion: wheel-zoom and MMB-drag also count
canvas.addEventListener('wheel', () => (tutFlags.camMoved = true), { passive: true });
canvas.addEventListener('pointerdown', (e) => {
  if (e.button === 1) tutFlags.camMoved = true;
});

// pause sim when tab is hidden (browsers throttle timers anyway — be explicit)
let speedBeforeHide = hudState.speed;
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    speedBeforeHide = hudState.speed;
    send({ type: 'setSpeed', speed: 0 });
  } else {
    send({ type: 'setSpeed', speed: speedBeforeHide as 0 | 1 | 2 | 3 });
  }
});

// ---- render loop ----

let last = performance.now();
let fpsAvg = 60;
let nextTutorialAt = 0;

function frame(now: number): void {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  fpsAvg = fpsAvg * 0.95 + (dt > 0 ? 1 / dt : 60) * 0.05;
  hudState.fps = fpsAvg;

  scene.render(now, dt);
  minimap.update(scene);
  if (now > nextTutorialAt) {
    nextTutorialAt = now + 500;
    tutorial.update(buildTutorialCtx());
  }

  // selection summary for the HUD panel (prunes dead/garrisoned ids too)
  if (scene.selectedUnits.size > 0) {
    const kinds = scene.kindsOf(scene.selectedUnits);
    const parts: string[] = [];
    for (const [kind, count] of [...kinds.entries()].sort((a, b) => a[0] - b[0])) {
      parts.push(`${count}× ${UNIT_NAMES[kind] ?? '?'}`);
    }
    hudState.selection =
      scene.selectedUnits.size > 0
        ? { count: scene.selectedUnits.size, label: parts.join(' · ') }
        : null;
  } else {
    hudState.selection = null;
  }

  hud.update(hudState);
  requestAnimationFrame(frame);
}

function fitCanvas(): void {
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  scene.resize();
}

window.addEventListener('resize', fitCanvas);
fitCanvas();
requestAnimationFrame(frame);
